<template>
  <div
    ref="containerRef"
    class="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-2 lg:gap-5 z-10"
  >
    <IngredientCard
      v-for="card in cards"
      :key="card.id"
      :title="card.title"
      :model-value="expandedCard === card.id"
      @update:modelValue="toggleCard(card.id)"
    >
      <template #expanded>
        <IngredientCardSection
          v-for="section in visibleSections(card.sections)"
          :key="section.title"
          :title="section.title"
          :items="section.items"
          :src="section.src"
        />
      </template>
    </IngredientCard>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import IngredientCard from './ingredient-card.vue'
import IngredientCardSection from './ingredient-card-section.vue'

export interface Props {
  page: 'home' | 'vegan'
}

const props = defineProps<Props>()

const expandedCard = ref<number | undefined>()
const containerRef = ref<HTMLElement | null>(null)

const cards = [
  {
    id: 1,
    title: '20g High-Quality Protein 💪',
    sections: [
      {
        title: 'Whey Protein',
        items: [ 'Fast Digesting', 'Low Sodium', 'Supports Muscle Growth & Recovery' ],
        src: 'https://cdn.shopify.com/s/files/1/1209/5722/files/protein-1.png?v=1753886475',
        hiddenOn: [ 'vegan' ]
      },
      {
        title: 'Pea Protein',
        items: [ 'Vegan', 'Lactose-Free', 'Cholesterol-Free' ],
        src: 'https://cdn.shopify.com/s/files/1/1209/5722/files/protein-2.png?v=1753886474'
      }
    ]
  },
  {
    id: 2,
    title: 'Whole-Grain Oats 🥣',
    sections: [
      {
        title: 'Gluten-Free Rolled Oats',
        items: [ 'Curbs Hunger', 'High-Fiber', 'Supports Digestion' ],
        src: 'https://cdn.shopify.com/s/files/1/1209/5722/files/whole.png?v=1753886474'
      }
    ]
  },
  {
    id: 3,
    title: 'Functional Nutrition 🌱',
    sections: [
      {
        title: 'Chia Seeds',
        items: [ 'High-Fiber', 'Rich in Omega 3s', 'Antioxidants' ],
        src: 'https://cdn.shopify.com/s/files/1/1209/5722/files/superfoods-1.png?v=1753886474'
      },
      {
        title: 'Flax Seed',
        items: [ 'High-Fiber', 'Helps Digestion', 'Keeps You Full for Longer' ],
        src: 'https://cdn.shopify.com/s/files/1/1209/5722/files/superfoods-2.png?v=1753886474'
      }
    ]
  },
  {
    id: 4,
    title: 'Real Food Ingredients 🍓',
    sections: [
      {
        title: 'For Unmatched Taste & Texture',
        items: [ 'Real Fruit', 'Real Cocoa', 'Real Spices' ],
        src: 'https://cdn.shopify.com/s/files/1/1209/5722/files/real-ingredients.png?v=1753886474'
      }
    ]
  }
]

const visibleSections = (sections: typeof cards[number]['sections']) => {
  return sections.filter(section => !section.hiddenOn?.includes(props.page))
}

const toggleCard = (id: number) => {
  expandedCard.value = expandedCard.value === id ? undefined : id
}

const handleClickOutside = (event: MouseEvent) => {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    expandedCard.value = undefined
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>
